import PropTypes from 'prop-types';
import React from 'react';

import { STICKY_COLUMN_POSITION } from 'ravenjs/constants/list';
import TableBody from 'ravenjs/lib/TableBody';
import TableCell from 'ravenjs/lib/TableCell';
import TableRow from 'ravenjs/lib/TableRow';
import Typography from 'ravenjs/lib/Typography';
import { callFunc, getHandler } from 'ravenjs/utils/actions';
import { get, isEmpty } from 'ravenjs/utils/lodash';
import { getZebraStyle, addStickyOffsetInColumns } from 'ravenjs/utils/table';
import styled, { withTheme } from 'styled-components';
import { sanitizeMarkup } from 'utils/sanitize';

const TypographyStyled = styled(Typography)`
    ${props => {
        const styles = get(props, 'theme.DataView.primaryText.styles', {});
        const fontFamily = styles.fontFamily ? `${styles.fontFamily} !important` : '';
        return {
            ...styles,
            fontFamily,
        };
    }}
`;

function DataListBody(props) {
    const { actions, data, schema, theme } = props;
    // Extract info out of the schema.
    const cellTypes = get(schema, 'cellTypes', {});
    let columns = get(schema, 'columns', []);
    const noData = get(schema, 'noData', 'No Data Available');
    const rowProps = get(schema, 'rows', {});
    const selectedProps = get(schema, 'selected', {});
    const tooltipTypes = get(schema, 'tooltipTypes', {});
    // Extract the renderSelectable func.
    const { renderSelectableRef = '', width: selectBoxWidth } = selectedProps;
    const renderSelectable = getHandler(actions, renderSelectableRef);
    const hasSelectColumn = typeof renderSelectable === 'function';
    const selectColumnWidth = selectBoxWidth || '50px';
    const hasStickyColumnsAtLeft = Boolean(
        columns.find(({ sticky }) => sticky && sticky.position === STICKY_COLUMN_POSITION.LEFT)
    );
    const hasStickyColumnsAtRight = Boolean(
        columns.find(({ sticky }) => sticky && sticky.position === STICKY_COLUMN_POSITION.RIGHT)
    );

    if (hasStickyColumnsAtLeft) {
        columns = addStickyOffsetInColumns(
            columns,
            STICKY_COLUMN_POSITION.LEFT,
            hasSelectColumn ? selectColumnWidth : '0px',
            theme
        );
    }

    if (hasStickyColumnsAtRight) {
        columns = addStickyOffsetInColumns(columns, STICKY_COLUMN_POSITION.RIGHT, '0px', theme);
    }

    if (isEmpty(data)) {
        // The table-cell colSpan will be updated based on if we're rendering the
        // selectable table cell.
        const colSpan =
            typeof renderSelectable === 'function' ? columns.length + 1 : columns.length;
        // Render the empty data table-body.
        return (
            <TableBody>
                <TableRow>
                    <TableCell colSpan={colSpan} noDataAvailable>
                        <Typography align="center" gutterBottom="0" gutterTop="0" type="subheading">
                            {noData}
                        </Typography>
                    </TableCell>
                </TableRow>
            </TableBody>
        );
    }

    return (
        <TableBody>
            {data.map((row, rowIdx) => {
                const { keyProp = 'id', ...restRowProps } = rowProps;
                const key = get(row, keyProp, `${keyProp}_${rowIdx}`);
                const { isExpandedRow } = row;
                const colSpan =
                    typeof renderSelectable === 'function' ? columns.length + 1 : columns.length;
                const specialColor = row.specialColorRow;

                return (
                    <TableRow
                        {...rowProps}
                        {...restRowProps}
                        handlers={actions}
                        key={key}
                        keyProp={keyProp}
                        row={row}
                        schema={schema}
                        specialColor={specialColor}
                        zebra={getZebraStyle(rowIdx, rowProps.zebra)}
                    >
                        {isExpandedRow && (
                            <TableCell colSpan={colSpan} noDataAvailable>
                                {row.expandedRowContent}
                            </TableCell>
                        )}
                        {!isExpandedRow && typeof renderSelectable === 'function' && (
                            <TableCell key={`${key}__checkbox`}>
                                {callFunc(renderSelectable, row)}
                            </TableCell>
                        )}
                        {!isExpandedRow &&
                            columns.map((bodyCol, bodyColIdx) => {
                                const {
                                    actions: bodyColActions = [],
                                    cellProps = {},
                                    ...restBodyCol
                                } = bodyCol;
                                const isNumeric = get(cellProps, 'isNumeric', false);
                                const defaultValue = isNumeric ? '0' : '-';
                                const bodyColValue = get(row, bodyCol.id, defaultValue);
                                const align = get(bodyCol, 'align', '');
                                const title = get(bodyCol, 'title', '');
                                const isSanitizedText = get(
                                    bodyCol,
                                    'titleProps.sanitizedText',
                                    false
                                );
                                let key =
                                    bodyColValue !== '-'
                                        ? bodyColValue
                                        : get(
                                              row,
                                              bodyCol.keyProp,
                                              `${bodyCol.keyProp || bodyCol.id}`
                                          );
                                key = `${key}_${bodyColIdx}`;

                                return (
                                    <TableCell
                                        actions={bodyColActions}
                                        align={align}
                                        cellProps={cellProps}
                                        cellTypes={cellTypes}
                                        col={restBodyCol}
                                        data-title={title}
                                        handlers={actions}
                                        key={key}
                                        row={row}
                                        schema={schema}
                                        tooltipTypes={tooltipTypes}
                                    >
                                        {isSanitizedText ? (
                                            <TypographyStyled
                                                gutterBottom="0"
                                                gutterTop="0"
                                                dangerouslySetInnerHTML={sanitizeMarkup(
                                                    bodyColValue
                                                )}
                                                fontSize="1rem"
                                            />
                                        ) : (
                                            <TypographyStyled
                                                gutterBottom="0"
                                                gutterTop="0"
                                                title={bodyColValue}
                                                fontSize="1rem"
                                            >
                                                {bodyColValue}
                                            </TypographyStyled>
                                        )}
                                    </TableCell>
                                );
                            })}
                    </TableRow>
                );
            })}
        </TableBody>
    );
}

DataListBody.propTypes = {
    actions: PropTypes.object,
    data: PropTypes.array,
    schema: PropTypes.object,
    theme: PropTypes.object.isRequired,
};

DataListBody.defaultProps = {
    actions: null,
    data: null,
    schema: null,
};

export { DataListBody as DataListBodyUnwrapped };
export default withTheme(DataListBody);
